import { ActionIcon, Badge, Box, Center, Flex, Group, Image, Menu, Pill, rem, Stack, Text } from '@mantine/core';
import { CalendarIcon, DotsThreeVerticalIcon, ImageIcon, MapPinIcon, PenIcon, TrashIcon } from '@phosphor-icons/react';
import type { FC, MouseEvent } from 'react';

import { capitalizeFirstLetter, emptyToUndefined } from '../../../helpers/stringHelpers.ts';
import { useI18n } from '../../../hooks/useI18n.ts';
import { formatDate } from '../../../lib/formatDate.ts';

export type EventPanelStatus = 'draft' | 'pending' | 'published' | 'declined' | 'archived';

interface BaseEventPanelProps {
  status: EventPanelStatus;
  imageSrc: string | null;
  title?: string;
  tags: string[];
  date: Date;
  declineReason?: string;
  onDropdownClick: (e: MouseEvent<HTMLButtonElement>, state: 'edit' | 'remove') => void;
}

interface LiveEventPanelProps extends BaseEventPanelProps {
  type: 'live';
  locationName: string;
}

interface NonLiveEventPanelProps extends BaseEventPanelProps {
  type?: 'secret' | 'online' | 'live';
  locationName?: string;
}

type EventPanelProps = LiveEventPanelProps | NonLiveEventPanelProps;

const statusToColor: Record<EventPanelStatus, { backgroundColor: string; dotColor: string }> = {
  draft: { dotColor: '#32CCDC', backgroundColor: 'blue.1' },
  pending: {
    dotColor: '#EE9F49',
    backgroundColor: '#FEEDDB',
  },
  published: {
    dotColor: '#80F130',
    backgroundColor: '#EDFBE7',
  },
  declined: {
    dotColor: '#E05252',
    backgroundColor: '#FDE8E8',
  },
  archived: {
    dotColor: '#9E9E9E',
    backgroundColor: '#F0F0F0',
  },
};

export const EventPanel: FC<EventPanelProps> = ({
  imageSrc,
  title,
  tags,
  date,
  locationName,
  status,
  declineReason,
  onDropdownClick,
  type,
}) => {
  const { t } = useI18n();

  return (
    <Box
      bg="white"
      c="gray.9"
      h="100%"
      p="xxs"
      pos="relative"
      style={{
        borderRadius: rem(16),
      }}
    >
      <Box
        bg={statusToColor[status].backgroundColor}
        c="black"
        fw="500"
        h="32"
        left={16}
        pos="absolute"
        px="12"
        py="5"
        style={{ borderRadius: 'var(--mantine-radius-sm)' }}
        top={16}
      >
        <Flex align="center" gap="xxxs">
          <Box bg={statusToColor[status].dotColor} h="8" style={{ borderRadius: '100%' }} w="8" />
          <Text size="xxs">{t(`event-status--${status}`) ?? status}</Text>
        </Flex>
      </Box>
      <Menu radius="xs" shadow="md">
        <Menu.Target>
          <ActionIcon
            color="gray.3"
            pos="absolute"
            right={15}
            size="lg"
            top={15}
            variant="outline"
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();
            }}
          >
            <DotsThreeVerticalIcon size={20} style={{ color: '#343330' }} />
          </ActionIcon>
        </Menu.Target>
        <Menu.Dropdown>
          <Menu.Item leftSection={<PenIcon size={22} />} onClick={(e) => onDropdownClick(e, 'edit')}>
            <Text size="xs">{t('event-panel_menu--edit') ?? 'Edit'}</Text>
          </Menu.Item>
          <Menu.Item leftSection={<TrashIcon size={22} />} onClick={(e) => onDropdownClick(e, 'remove')}>
            <Text size="xs"> {t('event-panel_menu--remove') ?? 'Remove'}</Text>
          </Menu.Item>
        </Menu.Dropdown>
      </Menu>

      <Box bg="white" mt={32 + 16}>
        {imageSrc ? (
          <Image
            alt="event-image"
            crossOrigin="anonymous"
            radius="sm"
            src={imageSrc}
            style={{ aspectRatio: '4/5', objectFit: 'cover' }}
            w="100%"
          />
        ) : (
          <Box
            bg="gigi-outlines"
            p="md"
            style={{ aspectRatio: '4/5', borderRadius: 'var(--mantine-radius-md)' }}
            w="100%"
          >
            <Center h="100%">
              <ImageIcon color="#C0C0C0" size={72} />
            </Center>
          </Box>
        )}
      </Box>
      {tags.length > 0 && (
        <Group gap="xxxs" mt="sm">
          {tags.map((tag) => (
            <Pill key={tag}>{capitalizeFirstLetter(tag)}</Pill>
          ))}
        </Group>
      )}

      <Text c="gray.9" fw={500} mt={12} size="md">
        {emptyToUndefined(title) ?? t('event-panel_message--no-title') ?? 'No title selected yet'}
      </Text>
      <Stack gap="xxxs" mt="xs">
        <Flex align="center" gap="xxxs">
          <CalendarIcon size={23} />
          <Text c="gray.6" fw={400} size="sm">
            {formatDate(date)}
          </Text>
        </Flex>

        <Flex align="center" gap="xxxs">
          <MapPinIcon size={23} />
          {type === undefined && (
            <Text c="gray.6" fw={400} size="sm">
              {emptyToUndefined(locationName) ?? t('event-panel_message--no-location') ?? 'Location not set'}
            </Text>
          )}
          {type === 'live' && (
            <Text c="gray.6" fw={400} size="sm">
              {emptyToUndefined(locationName) ?? t('event-panel_message--no-location') ?? 'Location not set'}
            </Text>
          )}
          {type === 'secret' && <Badge size="xs">{t('event-panel_option--secret') ?? 'Secret'}</Badge>}
          {type === 'online' && <Badge size="xs">{t('event-panel_option--online') ?? 'Online'}</Badge>}
        </Flex>
        {status === 'declined' && declineReason && (
          <Text c="red.6" fw={400} size="sm">
            {t('event-panel_label--decline-reason') ?? 'Reason'}: {declineReason}
          </Text>
        )}
      </Stack>
    </Box>
  );
};
